import * as React from 'react';

import {
  type AskNeedlActions,
  useAskNeedlState,
} from '@/features/ChatBotV2/hooks/useAskNeedlState';
import type { SessionState } from '@/features/ChatBotV2/types';
import type { BoardFileType } from '@/features/CustomFeedV2/common';
import type { ReportInfoType } from '@/features/NeedlReport/types';

export const AskNeedlContext = React.createContext<AskNeedlContextType | null>(
  null
);

export const AskNeedlDispatchContext =
  React.createContext<React.Dispatch<AskNeedlActions> | null>(null);

export const useAskNeedlContext = () => {
  const value = React.useContext(AskNeedlContext);

  if (!value) {
    throw new Error(
      'useAskNeedlContext must be used within a AskNeedlProvider'
    );
  }

  return value;
};

export const useAskNeedlDispatchContext = () => {
  const value = React.useContext(AskNeedlDispatchContext);

  if (!value) {
    throw new Error(
      'useAskNeedlDispatchContext must be used within a AskNeedlProvider'
    );
  }

  return value;
};

export const AskNeedlContextProvider: React.FunctionComponent<
  AskNeedlProviderType
> = ({
  initialState = {
    isLoading: false,
    isStreaming: false,
    searchWithin: 'feed' as SearchWithinType,
    currentFeedOrDocumentId: 'home',
    sessions: {
      feed: {
        [NEEDL_UNIVERSE_ID]: {
          messages: [],
          metadata: {
            type: NEEDL_UNIVERSE_ID,
            board_id: NEEDL_UNIVERSE_ID,
          },
        },
        home: {
          messages: [],
          attachments: [],
          metadata: {
            type: 'home',
            board_id: 'home',
          },
        },
        feed_id: {
          messages: [],
          metadata: {
            type: 'feed',
            channel_id: 'feed_id',
          },
        },
      },
      document: {
        document_id: {
          messages: [],
          metadata: {
            type: 'document',
            document_id: 'document_id',
          },
        },
      },
    },
    report: {
      enabled: false,
    },
    lastActiveSessionId: null,
  },
  children,
}) => {
  const [data, dispatch] = useAskNeedlState(initialState);

  const value = React.useMemo(() => ({ data }), [data]);

  return (
    <AskNeedlContext.Provider value={value}>
      <AskNeedlDispatchContext.Provider value={dispatch}>
        {children}
      </AskNeedlDispatchContext.Provider>
    </AskNeedlContext.Provider>
  );
};

export const NEEDL_UNIVERSE_ID = 'needl_universe';

export type AskNeedlContextType = {
  data: AskNeedlState;
};

export type AskNeedlState = {
  isLoading: boolean;
  isStreaming: boolean;
  searchWithin: SearchWithinType;
  currentFeedOrDocumentId: string;
  sessions: {
    [key in SearchWithinType]: {
      [key: string]: SessionState;
    };
  };
  report: AskNeedlReportType;
  lastActiveSessionId: string | null;
};

export type AskNeedlReportType = {
  enabled: boolean;
  info?: ReportInfoType;
  sources?: BoardFileType[];
};

type AskNeedlProviderType = {
  initialState?: AskNeedlState;
};

export type SearchWithinType = 'document' | 'feed';
